/**
 * @file analytics.js
 * @location src/integrations/analytics.js
 * @role 제품 분석 (Product Analytics) 추상 인터페이스 — PostHog / Mixpanel / GA4 진입점
 *
 * 백엔드/3rd-party 가 아직 없으므로 어댑터는 console + localStorage 버퍼 만.
 * 백엔드 도착 시:
 *   1) registerAnalyticsAdapter('posthog', { track: (event, props) => posthog.capture(event, props) })
 *   2) 버퍼된 과거 이벤트를 flush() 로 일괄 전송 (있다면)
 *   3) 호출부 변경 없음 — track() 만 살아있으면 즉시 동작
 *
 * 사용 예:
 *   import { track, EVENTS } from '../integrations/analytics';
 *   track(EVENTS.WIZARD_SHOWN);
 *   track(EVENTS.WIZARD_FINISHED);
 */

export const EVENTS = Object.freeze({
  // 앱 생애주기
  APP_OPENED:                'app.opened',

  // 온보딩 — 이름 1개 입력 후 멤버 탭 착지 (데모/스텝 분기 폐기, razor ㉒)
  WIZARD_SHOWN:              'wizard.shown',
  WIZARD_FINISHED:           'wizard.finished',

  // 핵심 첫 사용 — 활성화 funnel
  FIRST_TASK_ASSIGNED:       'first.task_assigned',
  FIRST_TASK_ACKNOWLEDGED:   'first.task_acknowledged',
  FIRST_TASK_COMPLETED:      'first.task_completed',
});

/**
 * @typedef {Object} AnalyticsAdapter
 * @property {string} name
 * @property {(event: string, props: object) => void|Promise<void>} track
 */

const BUFFER_KEY = 'shikomi.analytics.buffer';
const BUFFER_MAX = 500;  // 메모리/스토리지 폭주 방지

/** @type {AnalyticsAdapter[]} */
const adapters = [];

import { debug } from '../utils/debug.js';

const consoleAdapter = {
  name: 'console',
  track(event, props) {
    debug(`[analytics:${event}]`, props);
  },
};

const bufferAdapter = {
  name: 'buffer',
  track(event, props) {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(BUFFER_KEY);
      const buf = raw ? JSON.parse(raw) : [];
      buf.push({ event, props, at: Date.now() });
      if (buf.length > BUFFER_MAX) buf.splice(0, buf.length - BUFFER_MAX);
      localStorage.setItem(BUFFER_KEY, JSON.stringify(buf));
    } catch { /* quota·private mode */ }
  },
};

adapters.push(consoleAdapter, bufferAdapter);

export function registerAnalyticsAdapter(adapter) {
  if (!adapter || typeof adapter.track !== 'function') {
    throw new Error('analytics: adapter must implement track(event, props)');
  }
  const idx = adapters.findIndex(a => a.name === adapter.name);
  if (idx >= 0) adapters[idx] = adapter;
  else adapters.push(adapter);
}

export function listAnalyticsAdapters() {
  return adapters.map(a => a.name);
}

export function _resetAnalyticsAdapters() {
  adapters.length = 0;
  adapters.push(consoleAdapter, bufferAdapter);
}

/**
 * 버퍼에 쌓인 모든 이벤트 반환. 백엔드 연결 직후 일괄 전송 후 클리어 용.
 */
export function readBuffer() {
  if (typeof localStorage === 'undefined') return [];
  try {
    return JSON.parse(localStorage.getItem(BUFFER_KEY) || '[]');
  } catch {
    return [];
  }
}

/** 버퍼 비움 — 백엔드로 flush 한 직후 호출 */
export function clearBuffer() {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.removeItem(BUFFER_KEY); } catch { /* noop */ }
}

/**
 * 이벤트 발화. 어댑터별로 fire-and-forget — 실패해도 호출부 영향 없음.
 */
export function track(event, props = {}) {
  for (const a of adapters) {
    try {
      const r = a.track(event, props);
      if (r && typeof r.catch === 'function') r.catch(() => {});
    } catch {
      // adapter 실패는 호출부에 전파하지 않는다
    }
  }
}
